<template>
  <div class="key-info">
    <h3>Nøkkelinformasjon</h3>
    <div class="key-info__wrapper">
      <div
        class="key-info__properties"
        v-for="(item, index) in content"
        :key="index"
        :data-key="item.key"
        :style="{ '--column-width': getColumnSize(item) }"
      >
        <div class="key-info__label">{{ item.label }}</div>
        <div class="key-info__value" v-html="item.value"></div>
      </div>
    </div>
  </div>
</template>

<script>
const i18n = {
  gaardsnummer: 'Gårdsnr.',
  bruksnummer: 'Bruksnr.',
  kommunenummer: 'Kommunenr.',
  seksjonsnummer: 'Seksjonsnr.',
  oppdragsnummer: 'Oppdragsnummer',
  oppholdsrom: 'Oppholdsrom',
  ligningsverdi: 'Ligningsverdi',
  ligningsverdi2: 'Sekundær ligningsverdi',
  andelfellesformue: 'Andel fellesformue',
  'assignment-number': 'Oppdragsnummer',
  bnr: 'Bruksnr.',
  fnr: 'Festenr.',
  gnr: 'Gårdsnr.',
  knr: 'Kommunenr.',
  snr: 'Seksjonsnr.',
}

const sortMap = {
  Oppdragsansvarlig: 0,
  'Selger/Utbygger': 1,
  Betegnelse: 2,
  Oppdragsnummer: 3,
  Eier: 4,
  Primærrom: 5,
  Bruksareal: 6,
  'Gårdsnr.': 7,
  'Bruksnr.': 8,
  'Seksjonsnr.': 9,
  'Kommunenr.': 10,
  'Seksjonsnr.': 11,
  Byggeår: 12,
  Eiendomstype: 13,
  Eierform: 14,
  Tomteareal: 15,
  Energimerke: 16,
  Etasje: 17,
  Soverom: 18,
  Bad: 19,
  Oppholdsrom: 20,
  'Antall rom': 21,
  Fellesutgifter: 22,
  'Andel fellesformue': 23,
  'Andel fellesgjeld': 24,
  Ligningsverdi: 25,
  'Sekundær ligningsverdi': 26,
  Forretningsfører: 27,
}

export default {
  props: {
    data: {
      residentialProperties: Object,
      properties: Object,
      residentials: Array,
      metadataProperties: Object,
    },
  },

  methods: {
    getLabel(label) {
      const transFormedLabel = i18n[label]
      return transFormedLabel || label
    },
    numberWithSpaces(x) {
      if (!x) return

      return `<span style="white-space:nowrap">${x
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}</span>`
    },
    getColumnSize(item) {
      if (item.value.length > 200) return 3
      if (item.value.length > 50) return 2
      return 1
    },
  },

  computed: {
    jointDept() {
      const value = this.data.residentials
        .map(r => r.sale.jointDebt)
        .filter(Boolean)
        .sort((a, b) => a - b)

      let result = null

      if (!value.length && this.data.residentialProperties.jointDebt)
        result = `kr ${this.numberWithSpaces(this.data.residentialProperties.jointDebt.value)}`
      else if (value.length === 1) result = `kr ${this.numberWithSpaces(value[0])}`
      else if (value.length)
        result = `kr ${this.numberWithSpaces(value[0])} til kr ${this.numberWithSpaces(
          value[value.length - 1],
        )}`

      return {
        key: 'jointDebt',
        label: 'Andel fellesgjeld',
        value: result,
      }
    },

    jointCostsPerMonth() {
      const value = this.data.residentials
        .map(r => {
          const property = (r.properties || []).find(p => p.key === 'jointCostsPerMonth') || {}

          if (!property.value) return

          return parseInt(property.value.replace(/\D/g, ''))
        })
        .filter(Boolean)
        .sort((a, b) => a - b)

      let result = null

      if (!value.length && this.data.residentialProperties.jointDebtPerMonth)
        result = `kr ${this.numberWithSpaces(
          this.data.residentialProperties.jointDebtPerMonth.value,
        )} /mnd`
      else if (value.length === 1) result = `kr ${this.numberWithSpaces(value[0])} /mnd`
      else if (value.length)
        result = `kr ${this.numberWithSpaces(value[0])} til kr ${this.numberWithSpaces(
          value[value.length - 1],
        )} /mnd`

      return {
        key: 'jointDebtPerMonth',
        label: 'Fellesutgifter',
        value: result,
      }
    },

    transformMetadataProperties() {
      const properties = this.data.metadataProperties

      const {
        useableInternalArea,
        useableInternalAreaTo,
        primaryRoomArea,
        primaryRoomAreaTo,
        useableArea,
        useableAreaTo,
        plotArea,
        numberOfBedrooms,
        numberOfBedroomsTo,
        numberOfBaths,
        //  plotPropertyType,
        ownershipType,
        propertyType,
        energyLabel,
        floor,
        constuctionYear,
        numberOfRooms,
      } = this.data.residentialProperties

      if (!properties) return []

      function getProperty(property, residentialProperty, suffix) {
        return getValue(property, suffix) || getValue(residentialProperty, suffix)
      }

      function getValue(value, suffix) {
        if (!value.length || !value[0]) return null
        if (value.length === 1) return `${value[0]} ${suffix}`
        return `${value[0]} ${suffix} - ${value[value.length - 1]} ${suffix}`
      }

      const braI = getProperty(
        properties.useableInternalArea,
        [
          this.$path('value', useableInternalArea),
          this.$path('value', useableInternalAreaTo),
        ].filter(Boolean),
        'm²',
      )

      return [
        {
          key: 'constructionYear',
          label: 'Byggeår',
          value: properties.constructionYear[0] || this.$path('value', constuctionYear),
        },
        {
          key: 'energyLabel',
          label: 'Energimerke',
          value: properties.energyLabel[0] || this.$path('value', energyLabel),
        },
        {
          key: braI ? 'useableInternalArea' : 'primaryRoomArea',
          label: braI ? 'BRA-I' : 'Primærrom',
          value: braI
            ? braI
            : getProperty(
                properties.primaryRoomArea,
                [
                  this.$path('value', primaryRoomArea),
                  this.$path('value', primaryRoomAreaTo),
                ].filter(Boolean),
                'm²',
              ),
        },
        {
          key: 'useableArea',
          label: 'Bruksareal',
          value: getProperty(
            properties.useableArea,
            [this.$path('value', useableArea), this.$path('value', useableAreaTo)].filter(Boolean),
            'm²',
          ),
        },
        {
          key: 'plotArea',
          label: 'Tomteareal',
          value: getProperty(properties.plotArea, [this.$path('value', plotArea)], 'm²'),
        },

        {
          key: 'propertyType',
          label: 'Eiendomstype',
          value: properties.propertyType.length
            ? properties.propertyType.map(type => this.$tc(`propertyType:${type}`)).join(', ')
            : this.$path('value', propertyType),
        },

        {
          key: 'ownershipType',
          label: 'Eierform',
          value: properties.ownershipType.length
            ? properties.ownershipType.map(type => this.$t(`ownershipType:${type}`)).join(', ')
            : this.$path('value', ownershipType),
        },
        {
          key: 'numberOfBedrooms',
          label: 'Soverom',
          value: getProperty(
            properties.numberOfBedrooms,
            [this.$path('value', numberOfBedrooms), this.$path('value', numberOfBedroomsTo)].filter(
              Boolean,
            ),
            '',
          ),
        },
        {
          key: 'numberOfBathrooms',
          label: 'Bad',
          value: getProperty(
            properties.numberOfBathrooms,
            [this.$path('value', numberOfBaths)],
            '',
          ),
        },
        {
          key: 'floor',
          label: 'Etasje',
          value: getProperty(properties.floor, [this.$path('value', floor)], ''),
        },
        {
          key: 'numberOfRooms',
          label: 'Antall rom',
          value: getProperty(properties.numberOfRooms, [this.$path('value', numberOfRooms)], ''),
        },
      ].filter(p => p.value)
    },

    content() {
      let data = this.data.properties || {}

      if (data.owner) data.owner.label = 'Eier'

      return [
        ...Object.entries(data).map(([k, v]) => {
          return {
            key: k,
            value: v.value || v,
            label: this.getLabel(v.label || k),
          }
        }),
        ...[this.jointCostsPerMonth, this.jointDept].filter(p => p.value),
        ...this.transformMetadataProperties,
      ]
        .sort((a, b) => {
          return sortMap[a.label] - sortMap[b.label]
        })
        .filter(v => v.value)
    },
  },
}
</script>

<style lang="scss">
.key-info {
  @include respond-above('tablet') {
    padding-right: 2rem;
  }

  position: relative;

  @keyframes fill-height {
    from {
      height: 0;
    }
    to {
      height: 100%;
    }
  }

  // @include respond-above('tablet') {
  //   &:after {
  //     position: absolute;
  //     top: 0px;
  //     right: 0px;
  //     content: '';
  //     width: 1px;
  //     border: none;
  //     height: 0%;
  //     background-color: var(--primary);
  //     //    background: url('../../assets/vertical_line.svg') no-repeat;
  //     animation: 0.8s ease-in-out 0.6s 1 normal forwards running fill-height;
  //   }
  // }

  &__wrapper {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2rem;

    @include respond-above('tablet') {
      grid-template-columns: 1fr 1fr 1fr;

      .key-info__properties {
        grid-column-end: span var(--column-width);
      }
    }

    @include respond-below('phone') {
      gap: 1rem;
      grid-template-columns: 1fr;
    }
  }

  &__properties {
    // display: flex;
    // flex-direction: column;
  }
  &__label {
    font-weight: bold;
  }
  &__value {
    //color: var(--primary);
  }
}
</style>
